import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { RotateCcw, FileText, AlertTriangle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import MarkdownRenderer from './MarkdownRenderer';
import Layout from './Layout';

interface PitchFormData {
  startupName: string;
  shortDescription: string;
  fullDescription: string;
  fundingAmount: number;
  companyName: string;
  companyWebsite: string;
  investorOnlyContent: string;
  acceptTerms: boolean;
}

const PitchCreation: React.FC = () => {
  const navigate = useNavigate();
  const { themeClasses, isLightTheme } = useTheme();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  // @ts-ignore - Variable is used in JSX (lines 341, 344, 345, 349)
  const [showInvestorContent, setShowInvestorContent] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState<string[]>(['Alpha']);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [hasExistingPitch, setHasExistingPitch] = useState(false);
  const [existingPitchId, setExistingPitchId] = useState<number | null>(null);
  const [checkingExisting, setCheckingExisting] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<PitchFormData>();

  // const labels = ['Super', 'Alpha', 'Group A'];
  const labels = ['Alpha'];
  const acceptTerms = watch('acceptTerms');

  useEffect(() => {
    if (user?.accountType === 'founder') {
      checkExistingPitch();
    } else {
      setCheckingExisting(false);
    }
  }, [user]);

  const checkExistingPitch = async () => {
    try {
      setCheckingExisting(true);
      const response = await api.get('/pitches/check-existing');
      setHasExistingPitch(response.data.hasExistingPitch);
      setExistingPitchId(response.data.existingPitchId);
    } catch (error) {
      console.error('Error checking existing pitch:', error);
      toast.error('Failed to check existing pitch');
    } finally {
      setCheckingExisting(false);
    }
  };

  const handleLabelToggle = (label: string) => {
    setSelectedLabels(prev => 
      prev.includes(label) 
        ? prev.filter(l => l !== label)
        : [...prev, label]
    );
  };

  const handleReset = () => {
    setShowResetDialog(true);
  };

  const confirmReset = () => {
    reset();
    setSelectedLabels(['Alpha']);
    setShowInvestorContent(false);
    setShowResetDialog(false);
    toast.success('Form reset successfully');
  };

  const handleShowTerms = async () => {
    try {
      const response = await api.get('/admin/terms');
      setTermsContent(response.data.terms);
      setShowTermsDialog(true);
    } catch (error) {
      console.error('Error fetching terms:', error);
      toast.error('Failed to load terms and conditions');
    }
  };

  const onSubmit = async (data: PitchFormData) => {
    if (!data.acceptTerms) {
      toast.error('You must accept the Terms and Conditions to create a pitch');
      return;
    }

    if (hasExistingPitch) {
      toast.error('You already have an existing pitch. Please delete your current pitch before creating a new one.');
      return;
    }

    setIsLoading(true);
    try {
      const pitchData = {
        ...data,
        labels: selectedLabels,
        isPublic: true
      };

      await api.post('/pitches', pitchData);
      toast.success('Pitch created successfully!');
      navigate('/');
    } catch (error: any) {
      console.error('Error creating pitch:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to create pitch');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingExisting) {
    return (
      <div className={`min-h-screen ${themeClasses.background} flex items-center justify-center`}>
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={themeClasses.textSecondary}>Checking existing pitch...</p>
        </div>
      </div>
    );
  }

  if (hasExistingPitch) {
    return (
      <Layout title="Create Pitch" showBackButton onBack={() => navigate('/')}>

        {/* Existing Pitch Warning */}
        <div className="p-4">
          <div className={`${themeClasses.card} border-l-4 border-yellow-500`}>
            <div className="flex items-start">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-4 mt-1">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-2`}>
                  Existing Pitch Found
                </h3>
                <p className={`${themeClasses.textSecondary} mb-4`}>
                  You already have an existing pitch. You can only create one pitch at a time. 
                  Please delete your current pitch before creating a new one.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => navigate(`/pitch/${existingPitchId}`)}
                    className={`${themeClasses.button} px-4 py-2 rounded-lg`}
                  >
                    View My Pitch
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className={`${themeClasses.buttonSecondary} px-4 py-2 rounded-lg`}
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Create Pitch" showBackButton onBack={() => navigate('/')}>
      {/* Reset button - we'll add this to header later if needed */}
      <div className={`${themeClasses.card} border-b ${themeClasses.border} p-4 flex justify-between mx-4`}>
        
      {/* Label Selection */}
      <div className={``}>
        <h3 className={`text-sm font-medium ${themeClasses.textSecondary} mb-3`}>Select Labels</h3>
        <div className="flex flex-wrap gap-2">
          {labels.map((label) => (
            <button
              key={label}
              onClick={() => handleLabelToggle(label)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                selectedLabels.includes(label)
                  ? 'bg-primary-500 text-white'
                  : isLightTheme 
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
        <button
          onClick={handleReset}
          className={`flex items-center ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors mb-4`}
          title="Reset form"
        >
          <RotateCcw className="w-5 h-5 mr-2" />
          Reset Form
        </button>
      </div>

      

      {/* Form */}
      <div className="p-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Main Content */}
          <div className={`${themeClasses.card} rounded-lg`}>
            <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>Pitch Details</h3>
            
            {/* Startup Name */}
            <div className="mb-4">
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                Startup Name *
              </label>
              <input
                type="text"
                {...register('startupName', { 
                  required: 'Startup name is required',
                  maxLength: { value: 255, message: 'Startup name must be less than 255 characters' }
                })}
                className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                placeholder="Enter your startup name"
              />
              {errors.startupName && (
                <p className="text-red-400 text-sm mt-1">{errors.startupName.message}</p>
              )}
            </div>

            {/* Short Description */}
            <div className="mb-4">
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                Short Description *
              </label>
              <textarea
                {...register('shortDescription', { 
                  required: 'Short description is required',
                  maxLength: { value: 500, message: 'Short description must be less than 500 characters' }
                })}
                className={`${themeClasses.input} w-full h-20 resize-none rounded-lg px-3 py-2`}
                placeholder="Brief description of your startup (max 500 characters)"
              />
              {errors.shortDescription && (
                <p className="text-red-400 text-sm mt-1">{errors.shortDescription.message}</p>
              )}
            </div>

            {/* Full Description */}
            <div className="mb-4">
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                Full Description *
              </label>
              <textarea
                {...register('fullDescription', { 
                  required: 'Full description is required'
                })}
                className={`${themeClasses.input} w-full h-32 resize-none rounded-lg px-3 py-2`}
                placeholder="Detailed description of your startup, problem, solution, market, etc."
              />
              {errors.fullDescription && (
                <p className="text-red-400 text-sm mt-1">{errors.fullDescription.message}</p>
              )}
            </div>

            {/* Funding Amount */}
            <div className="mb-4">
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                Funding Amount (USD) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                {...register('fundingAmount', { 
                  required: 'Funding amount is required',
                  min: { value: 0, message: 'Funding amount must be positive' }
                })}
                className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                placeholder="500000"
              />
              {errors.fundingAmount && (
                <p className="text-red-400 text-sm mt-1">{errors.fundingAmount.message}</p>
              )}
            </div>

            {/* Company Name */}
            <div className="mb-4">
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                Company Name
              </label>
              <input
                type="text"
                {...register('companyName')}
                className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                placeholder="Your company name (optional)"
              />
            </div>

            {/* Company Website */}
            <div className="mb-4">
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                Company Website
              </label>
              <input
                type="url"
                {...register('companyWebsite')}
                className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                placeholder="https://yourcompany.com"
              />
            </div>
          </div>

          {/* Investor Content */}
          {/* <div className={themeClasses.card}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${themeClasses.text}`}>Investor-Only Content</h3>
              <button
                type="button"
                onClick={() => setShowInvestorContent(!showInvestorContent)}
                className={`flex items-center ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors`}
              >
                {showInvestorContent ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                {showInvestorContent ? 'Hide' : 'Show'}
              </button>
            </div>
            
            {showInvestorContent && (
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Investor-Only Content
                </label>
                <textarea
                  {...register('investorOnlyContent')}
                  className={`${themeClasses.input} w-full h-32 resize-none rounded-lg px-3 py-2`}
                  placeholder="Additional content visible only to investors (financial projections, detailed business plan, etc.)"
                />
                <p className={`${themeClasses.textSecondary} text-sm mt-1`}>
                  This content will only be visible to founder accounts
                </p>
              </div>
            )}
          </div> */}

          {/* Terms and Conditions */}
          <div className={`${themeClasses.card} rounded-lg`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${themeClasses.text}`}>Terms and Conditions</h3>
              <button
                type="button"
                onClick={handleShowTerms}
                className={`flex items-center ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors`}
              >
                <FileText className="w-4 h-4 mr-1" />
                View Terms
              </button>
            </div>
            
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                {...register('acceptTerms', { 
                  required: 'You must accept the Terms and Conditions' 
                })}
                className="mt-1 w-4 h-4 text-primary-500 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
              />
              <div>
                <label className={`text-sm ${themeClasses.textSecondary}`}>
                  I accept the{' '}
                  <button
                    type="button"
                    onClick={handleShowTerms}
                    className="text-primary-400 hover:text-primary-300 underline"
                  >
                    Terms and Conditions
                  </button>
                  {' '}of this website
                </label>
                {errors.acceptTerms && (
                  <p className="text-red-400 text-sm mt-1">{errors.acceptTerms.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !acceptTerms}
            className={`${themeClasses.button} w-full py-3 rounded-lg font-medium transition-colors duration-200 ${
              !acceptTerms ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? 'Creating Pitch...' : 'Create Pitch'}
          </button>
        </form>
      </div>

      {/* Terms and Conditions Dialog */}
      {showTermsDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.card} max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 rounded-lg`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-semibold ${themeClasses.text}`}>Terms and Conditions</h2>
              <button
                onClick={() => setShowTermsDialog(false)}
                className={`${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700`}
              >
                ✕
              </button>
            </div>
            <div className="prose prose-lg max-w-none">
              <MarkdownRenderer 
                content={termsContent || 'Terms and conditions content will be displayed here.'} 
              />
            </div>
            <div className="mt-6 flex justify-end border-t pt-4">
              <button
                onClick={() => setShowTermsDialog(false)}
                className={`${themeClasses.buttonSecondary} px-6 py-2 rounded-lg`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      {showResetDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.card} max-w-md w-full max-h-[80vh] overflow-y-auto rounded-lg`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-xl font-semibold ${themeClasses.text}`}>Confirm Reset</h2>
              <button
                onClick={() => setShowResetDialog(false)}
                className={`${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors`}
              >
                ✕
              </button>
            </div>
            <p className={`${themeClasses.textSecondary} mb-4`}>
              Are you sure you want to reset the form? This will clear all your input fields and selected labels.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowResetDialog(false)}
                className={`${themeClasses.buttonSecondary} px-4 py-2 rounded-lg`}
              >
                Cancel
              </button>
              <button
                onClick={confirmReset}
                className={`${themeClasses.button} px-4 py-2 rounded-lg`}
              >
                Reset Form
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PitchCreation; 